import type { Profile } from 'models';
import { useEffect, useState } from 'react';
import { Flex, Form, FormActions, Modal, Select, Text } from 'ui';
import { timezones } from 'common/utils';
import { useConfirmTimezoneMutation } from './queries';

const timeZoneModalShowed = 'timeZoneModalShowed';

type P = {
  navigateToChangeTimezone: (id: string) => Promise<void>;
  viewer: Profile;
};

type TimezoneConfirmationModalFormValues = {
  timeZone: string;
};

const TimezoneConfirmationModal = ({ navigateToChangeTimezone, viewer }: P) => {
  const [isOpened, setIsOpened] = useState(false);
  // eslint-disable-next-line @typescript-eslint/naming-convention -- This is named by backend API
  const { id, requiredConsents, timeZone, timeZoneChanged } = viewer;

  const { isPending: isConfirmLoading, mutate: confirmTimezone } = useConfirmTimezoneMutation();

  useEffect(() => {
    if (
      timeZone &&
      timeZoneChanged !== null &&
      !timeZoneChanged &&
      sessionStorage.getItem(timeZoneModalShowed) !== 'true'
    ) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- TODO: fix later
      setIsOpened(true);
      sessionStorage.setItem(timeZoneModalShowed, 'true');
    }
  }, [timeZone, timeZoneChanged]);

  if (!isOpened) {
    return null;
  }

  if (requiredConsents.some((consent) => !consent.consented) || !timeZone) {
    sessionStorage.removeItem(timeZoneModalShowed);
    return null;
  }

  const handleConfirm = () => {
    confirmTimezone(
      {
        timeZone,
        userId: id,
      },
      {
        onSuccess() {
          setIsOpened(false);
        },
      },
    );
  };

  const handleGoToProfile = async () => {
    await navigateToChangeTimezone(id);
    setIsOpened(false);
  };

  const handleClose = () => {
    if (isConfirmLoading) {
      return;
    }

    setIsOpened(false);
  };

  return (
    <Modal closable={false} isOpened={isOpened} onCancel={handleClose} title="Confirm your timezone" width="medium">
      <Modal.HugContent>
        <Flex gap="large" vertical>
          <Text>Please confirm, that you’re currently located in the timezone displayed below.</Text>
          <Form initialValues={{ timeZone }} key={`${id}-${timeZone}`}>
            <Form.Item<TimezoneConfirmationModalFormValues> label="Timezone" name="timeZone" noOptional>
              <Select
                disabled
                optionFilterProp="label"
                options={timezones.map((timezone) => ({
                  label: timezone.label,
                  value: timezone.value,
                }))}
                showSearch
              />
            </Form.Item>
          </Form>
        </Flex>

        <Text>You can change the timezone in your profile.</Text>

        <Modal.HugContent.Footer>
          <FormActions
            right={{
              children: 'Go to your profile',
              disabled: isConfirmLoading,
              onClick() {
                void handleGoToProfile();
              },
              size: 'medium',
            }}
            submit={{
              children: 'Confirm timezone',
              loading: isConfirmLoading,
              onClick: handleConfirm,
              size: 'medium',
            }}
          />
        </Modal.HugContent.Footer>
      </Modal.HugContent>
    </Modal>
  );
};

export default TimezoneConfirmationModal;
